/**
 * Setup-time rule table — the browser-side half.
 *
 * Mirrors scheduling_system/unified/setup_rules.py: same vocabulary, same
 * bottom-up first-match-wins order, same readable sentence. It exists so the
 * panel can explain and validate a rule as it is typed, without a round-trip.
 *
 * The Python side remains the authority. Anything this file rejects, the
 * optimizer would also reject — but the reverse is not guaranteed, so a save
 * still has to survive parse_setup_time_rules().
 */

export const SUBJECTS = [
  { value: 'this', label: 'this job' },
  { value: 'previous', label: 'the job before' },
  { value: 'position', label: 'where it sits' },
];

// `label` is what the dropdown offers, `sentence` is how the rule reads back in
// the summary line and in the Excel — "first job in a shift" is the right thing
// to pick from a list and the wrong thing to read in a sentence.
export const POSITIONS = [
  { value: 'first_in_shift', label: 'first job in a shift', sentence: 'it is the first job of the shift' },
  { value: 'mid_shift', label: 'anywhere in the shift except the first job', sentence: 'it is not the first job of the shift' },
  { value: 'first_on_machine', label: 'first job on the machine', sentence: 'it is the first job on the machine' },
  { value: 'back_to_back', label: 'the shift follows the previous one with no gap', sentence: 'the shift follows the previous one with no gap' },
];

/**
 * The positions offered when building a rule — only the two that are about
 * where in the shift the job sits (Adi, 2026-10-14).
 *
 * The other two stay in POSITIONS because rules already use them: ROT, 100L and
 * 110L all have a `back_to_back` line. positionChoices() therefore keeps
 * whatever a rule is already set to, so opening an existing rule never silently
 * rewrites it to something the dropdown happens to offer.
 */
export const OFFERED_POSITIONS = ['first_in_shift', 'mid_shift'];

export function positionChoices(current) {
  const wanted = OFFERED_POSITIONS.includes(current)
    ? OFFERED_POSITIONS : [...OFFERED_POSITIONS, current];
  return POSITIONS.filter(p => wanted.includes(p.value));
}

export const OPS = [
  { value: 'is', label: 'is' },
  { value: 'is_not', label: 'is not' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'same_as_this', label: 'is the same as this job' },
  { value: '>', label: 'is more than' },
  { value: '<', label: 'is less than' },
];

export const NUMERIC_OPS = ['>', '<', '>=', '<='];

/**
 * The five places a setup time can come from. What the two opaque ones mean,
 * checked against the code that computes them rather than their names:
 *   from_attributes -> timing.py _attribute_setup(): the LARGEST of the
 *                      penalties of whatever differs from the job before.
 *   startup         -> shift.startup from global_rules.yaml, a flat number of
 *                      hours at the start of a shift, one value for every
 *                      machine (0 today).
 * That belongs here, not on the screen — Adi, 2026-10-14.
 */
export const SOURCES = [
  { value: 'hours', label: 'hours' },
  { value: 'from_input', label: "the input file's own setup time" },
  { value: 'startup', label: 'the fixed start-of-shift time' },
  { value: 'formula', label: 'calculated from the rolls' },
  { value: 'from_attributes', label: 'the normal changeover time' },
];

export const ENGINES = ['cp-sat', 'ga'];

/** Which `then` key a rule is using. */
export function sourceKind(rule) {
  const then = (rule && rule.then) || {};
  if ('hours' in then) return 'hours';
  if (then.from_input) return 'from_input';
  if (then.shift_startup || then.plus_from_input) return 'startup';
  if ('formula' in then) return 'formula';
  return 'from_attributes';
}

export function describeSource(rule) {
  const then = (rule && rule.then) || {};
  switch (sourceKind(rule)) {
    case 'hours': return `${then.hours} hours`;
    case 'from_input': return "the input file's own setup time";
    case 'startup':
      return then.plus_from_input
        ? 'the fixed start-of-shift time, plus the input file'
        : 'the fixed start-of-shift time';
    case 'formula':
      // The row's one-liner. The full working is shown under the option when
      // the rule is open; this just has to be more than "the rolls".
      return 'calculated from the rolls (first roll + extra per roll)';
    default: return 'the normal changeover time';
  }
}

function describeCondition(cond) {
  if (!cond) return '';
  if (cond.subject === 'position') {
    const found = POSITIONS.find(p => p.value === cond.is);
    return found ? found.sentence : `the position is ${cond.is}`;
  }
  const who = cond.subject === 'this' ? 'this job' : 'the job before';
  if (cond.op === 'same_as_this') {
    return `${who} has the same ${cond.field} as this job`;
  }
  const values = Array.isArray(cond.is) ? cond.is
    : (cond.value !== undefined ? [cond.value] : (cond.is !== undefined ? [cond.is] : []));
  if (NUMERIC_OPS.includes(cond.op)) {
    return `${who}'s ${cond.field} is ${cond.op} ${values[0]}`;
  }
  const joined = values.map(v => (v === null || v === '' ? '(empty)' : v)).join(' or ');
  const verb = cond.op === 'is_not' ? 'is not'
    : cond.op === 'starts_with' ? 'starts with' : 'is';
  return `${who}'s ${cond.field} ${verb} ${joined}`;
}

/** The rule's `when` as one readable sentence — the same text the Excel shows. */
export function describeRule(rule) {
  const when = (rule && rule.when) || [];
  if (!when.length) return 'anything else';
  return when.map(describeCondition).join(' and ');
}

/** The value list a condition carries, normalised for editing. */
export function conditionValues(cond) {
  if (!cond) return [];
  if (cond.op === 'same_as_this') return [];
  if (Array.isArray(cond.is)) return cond.is;
  if (cond.value !== undefined) return [cond.value];
  if (cond.is !== undefined) return [cond.is];
  return [];
}

export function newCondition() {
  return { subject: 'this', field: 'guideline', op: 'is', is: [] };
}

export function newRule() {
  // No work-center prefix. The name is what the customer typed; the work center
  // is added when the Excel is written, so it is not repeated on every row of a
  // panel that already says which machine it belongs to.
  return {
    name: '',
    when: [newCondition()],
    then: { hours: 1 },
    enabled: true,
  };
}

/**
 * Rules that no job can reach.
 *
 * The table is read bottom-up, so a rule is unreachable when one BELOW it is
 * strictly more general. A rule restricted to one engine cannot hide a rule the
 * other engine still reaches — mirrors find_unreachable_rules() in Python, and
 * getting that wrong once flagged four healthy work centers as broken.
 */
export function findUnreachable(rules) {
  const key = c => JSON.stringify([c.subject, c.field, c.op,
    (conditionValues(c) || []).map(String).sort(), c.is]);
  const sets = rules.map(r => new Set(((r && r.when) || []).map(key)));
  const engines = rules.map(r => new Set(
    (r && r.engines && r.engines.length) ? r.engines : ENGINES));
  const out = [];
  rules.forEach((rule, i) => {
    if (rule && rule.enabled === false) return;
    for (let j = rules.length - 1; j > i; j -= 1) {
      if (rules[j] && rules[j].enabled === false) continue;
      const subset = [...sets[j]].every(k => sets[i].has(k));
      const engineSubset = [...engines[i]].every(e => engines[j].has(e));
      if (subset && engineSubset) { out.push({ index: i, shadowedBy: j }); return; }
    }
  });
  return out;
}

/**
 * Everything wrong with a table, as {level, index, message}.
 *
 * `level` is 'error' (refuse to save) or 'warning' (say so and allow). The
 * split matters: a value that is simply not in this month's dispatch list is
 * perfectly legitimate, while an attribute the machine does not have is a rule
 * that can never fire.
 *
 * This runs ON SAVE ONLY, never while a rule is being typed. A rule under
 * construction is always invalid — no value yet, no attribute chosen yet — so
 * checking as you type can do nothing but accuse the user of a mistake they
 * have not made. Adi's instruction, 2026-10-14.
 */
export function validateTable(rules, { knownFields = [], knownValues = {}, hasMsaBlock = false } = {}) {
  const issues = [];
  if (!rules || !rules.length) {
    issues.push({ level: 'error', index: null, message: 'The table needs at least a fallback rule.' });
    return issues;
  }

  const first = rules[0];
  if (((first && first.when) || []).length) {
    issues.push({
      level: 'error', index: 0,
      message: 'Line 1 is the one that applies to every job none of the rules below fit, '
        + 'so it cannot have conditions of its own. Without it a job can fit nothing and be charged no setup at all.',
    });
  }
  if (first && first.enabled === false) {
    issues.push({ level: 'error', index: 0, message: 'Line 1 cannot be switched off.' });
  }

  const seen = new Map();
  rules.forEach((rule, i) => {
    const name = (rule.name || '').trim();
    if (name) {
      const k = name.toUpperCase();
      if (seen.has(k)) {
        issues.push({
          level: 'error', index: i,
          message: `Another rule is already called "${name}". Names identify a rule in the Excel, so they must be unique.`,
        });
      }
      seen.set(k, i);
    }

    if (sourceKind(rule) === 'formula' && !hasMsaBlock) {
      // WARNING, not an error. The panel is not always given the roll settings
      // block, so treating its absence as fact blocked saving a perfectly good
      // ROT table — the machine has roll settings, the browser just could not
      // see them. The optimizer checks this authoritatively and refuses there.
      issues.push({
        level: 'warning', index: i,
        message: 'This rule uses the roll calculation. If this machine has no roll settings the rule will not fire.',
      });
    }
    if (sourceKind(rule) === 'hours' && !(Number(rule.then.hours) >= 0)) {
      issues.push({ level: 'error', index: i, message: 'Hours must be zero or more.' });
    }

    ((rule.when) || []).forEach((cond, ci) => {
      if (cond.subject === 'position') return;
      if (!cond.field) {
        issues.push({
          level: 'error', index: i,
          message: `Condition ${ci + 1}: choose which attribute to compare.`,
        });
        return;
      }
      if (knownFields.length && !knownFields.includes(cond.field)) {
        issues.push({
          level: 'error', index: i,
          message: `This machine has no "${cond.field}" to compare, so the rule could never fire.`,
        });
        return;
      }
      if (cond.op === 'same_as_this') {
        if (cond.subject !== 'previous') {
          issues.push({
            level: 'error', index: i,
            message: '"is the same as this job" only means something for the job before.',
          });
        }
        return;
      }
      const values = conditionValues(cond);
      if (!values.length) {
        issues.push({
          level: 'error', index: i,
          message: `Condition ${ci + 1}: type the value it has to match.`,
        });
        return;
      }
      if (NUMERIC_OPS.includes(cond.op) && !values.every(v => v !== '' && !Number.isNaN(Number(v)))) {
        issues.push({ level: 'error', index: i, message: `Condition ${ci + 1} needs a number.` });
        return;
      }
      const known = knownValues[cond.field];
      if (known && known.length && !NUMERIC_OPS.includes(cond.op)) {
        const missing = values.filter(
          v => !known.some(k => String(k).toUpperCase() === String(v).toUpperCase()));
        if (missing.length) {
          issues.push({
            level: 'warning', index: i,
            message: `${missing.join(', ')} ${missing.length > 1 ? 'are' : 'is'} not in the dispatch list that is loaded. `
              + 'That is fine if it simply has not come up this month.',
          });
        }
      }
    });
  });

  findUnreachable(rules).forEach(({ index, shadowedBy }) => {
    const other = rules[shadowedBy];
    issues.push({
      level: 'warning', index,
      message: `This rule can never fire: "${(other.name || describeRule(other))}" is below it and matches everything this one does. `
        + 'The table is read from the bottom up.',
    });
  });

  return issues;
}

/**
 * Rules copied to another machine, re-prefixed and checked against the target.
 *
 * Nothing is dropped silently: a rule the target cannot evaluate is copied
 * DISABLED and reported, so it is visible and one click from working rather
 * than quietly absent.
 */
export function copyRulesTo(rules, fromWc, toWc, target, mode = 'append') {
  const { existing = [], knownFields = [], hasMsaBlock = false } = target;
  const notes = [];
  const taken = new Set(existing.map(r => (r.name || '').trim().toUpperCase()).filter(Boolean));

  const copied = rules
    .filter(r => ((r.when) || []).length)   // the target keeps its own fallback
    .map(rule => {
      const out = JSON.parse(JSON.stringify(rule));

      // Names carry no work-center prefix, so a copy only has to avoid
      // colliding with a name the target already uses.
      const base = (out.name || '').trim();
      if (base) {
        let name = base;
        let n = 2;
        while (taken.has(name.toUpperCase())) { name = `${base} (${n})`; n += 1; }
        taken.add(name.toUpperCase());
        out.name = name;
      }

      const missingField = ((out.when) || [])
        .filter(c => c.subject !== 'position' && c.field)
        .map(c => c.field)
        .find(f => knownFields.length && !knownFields.includes(f));
      if (missingField) {
        out.enabled = false;
        notes.push(`"${out.name || describeRule(out)}" was copied switched off — ${toWc} has no ${missingField}.`);
      } else if (sourceKind(out) === 'formula' && !hasMsaBlock) {
        out.enabled = false;
        notes.push(`"${out.name || describeRule(out)}" was copied switched off — ${toWc} has no roll settings.`);
      }
      return out;
    });

  const fallback = existing.length ? [existing[0]] : [{ name: null, when: [], then: { from_attributes: true } }];
  const rest = mode === 'replace' ? [] : existing.slice(1);
  return { rules: [...fallback, ...rest, ...copied], notes, copiedCount: copied.length };
}

/**
 * Which rule would fire for a made-up job, and which others also matched.
 *
 * The panel states that the table is read bottom-up and warns about rules that
 * can NEVER fire, but neither tells you what happens to a job that satisfies two
 * rules at once — and that is the question people actually ask. Until a run
 * produced an Excel there was no way to see it.
 *
 * `job` is {field: value}, plus `position` as a list of flags. Mirrors
 * setup_hours() in Python: first match from the bottom wins.
 */
export function explainJob(rules, job, engine = 'ga') {
  const position = job.position || [];
  const norm = v => (v === undefined || v === null || v === '' ? null : String(v).trim().toUpperCase());

  const fits = cond => {
    if (cond.subject === 'position') return position.includes(cond.is);
    // "the job before" is only meaningful once something ran before it
    if (cond.subject === 'previous' && !job.previous) return false;
    const src = cond.subject === 'this' ? job : job.previous;
    if (cond.op === 'same_as_this') return norm(src[cond.field]) === norm(job[cond.field]);
    const raw = src[cond.field];
    const values = conditionValues(cond);
    if (NUMERIC_OPS.includes(cond.op)) {
      const n = Number(raw);
      if (Number.isNaN(n)) return false;
      const t = Number(values[0]);
      return cond.op === '>' ? n > t : cond.op === '<' ? n < t
        : cond.op === '>=' ? n >= t : n <= t;
    }
    const v = norm(raw);
    if (cond.op === 'starts_with') return v !== null && values.some(x => v.startsWith(norm(x)));
    if (cond.op === 'is_not') return !values.some(x => norm(x) === v);
    return values.some(x => norm(x) === v);
  };

  const matched = [];
  rules.forEach((rule, index) => {
    if (rule.enabled === false) return;
    if (rule.engines && rule.engines.length && !rule.engines.includes(engine)) return;
    if (((rule.when) || []).every(fits)) matched.push({ index, rule });
  });

  if (!matched.length) return { winner: null, alsoMatched: [] };
  const winner = matched[matched.length - 1];          // bottom-most wins
  return { winner, alsoMatched: matched.slice(0, -1).reverse() };
}
